import { computed, reactive } from 'vue';
import { useQuery, useQueryCache } from '@zoomcatalog/query-client';
import { AxiosError } from 'axios';

import api from '@/config/axios';
import { GET_COMPANY, GET_COMPANY_BY_ID } from '@/config/endpoints';
import { useCommit, useSelector } from '@/store/rootStore';
import { Company } from '../ts-definitions';

type CompanyQueryContext = {
  options: {
    companyId: string | undefined;
  };
  handlers?: {
    onSuccess?(data: Company): void;
  };
};
export function useCompanyQuery(context: CompanyQueryContext) {
  const endpoints = useSelector((state) => state.sniffer.endpoints);
  const companyId = computed(() => context.options.companyId ?? '');

  return useQuery<Company, AxiosError>(
    'company',
    reactive({
      queryFn: async () =>
        api
          .get<Company>(GET_COMPANY.endpoint, {
            headers: {
              'X-Zoom-Company': companyId.value,
            },
            baseURL: endpoints.value.COMPANIES_API_ENDPOINT,
          })
          .then((data) => data.data),
      onSuccess(data) {
        context?.handlers?.onSuccess?.(data);
      },
      enabled: companyId,
      staleTime: Infinity,
      refetchOnMount: false,
    })
  );
}

export function useCompanyByIdQuery(context: CompanyQueryContext) {
  const endpoints = useSelector((state) => state.sniffer.endpoints);
  const companyId = computed(() => context.options.companyId ?? '');
  const commit = useCommit();

  return useQuery<Company, AxiosError>(
    ['company', companyId.value],
    reactive({
      queryFn: async () =>
        api
          .get<Company>(GET_COMPANY_BY_ID.endpoint(companyId.value), {
            baseURL: endpoints.value.COMPANIES_API_ENDPOINT,
          })
          .then((data) => data.data),
      onSuccess(data) {
        commit({ type: 'auth/setCompany', payload: data });
        context?.handlers?.onSuccess?.(data);
      },
      enabled: companyId,
      staleTime: Infinity,
      refetchOnMount: false,
    })
  );
}

export function useInvalidateCompanyQuery() {
  const cache = useQueryCache();
  return async () => cache.invalidateQueries('company');
}
